import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  getOfflineDraft,
  isNetworkError,
  listOfflineDrafts,
  listPendingOfflineEntryIds,
  removeOfflineDraft,
  updateOfflineDraftStatus
} from '../utils/offlineTechlogStore';

/**
 * Online/offline status + flush pending techlog drafts to the server.
 * @param {{ onSynced?: (entryId: string) => void, onSyncError?: (entryId: string, error: unknown) => void, enabled?: boolean }} [options]
 */
export function useOfflineTechlogSync(options = {}) {
  const { onSynced, onSyncError, enabled = true } = options;
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingEntryIds, setPendingEntryIds] = useState(() => new Set());
  const [syncing, setSyncing] = useState(false);
  const syncingRef = useRef(false);
  const onSyncedRef = useRef(onSynced);
  const onSyncErrorRef = useRef(onSyncError);

  useEffect(() => {
    onSyncedRef.current = onSynced;
    onSyncErrorRef.current = onSyncError;
  }, [onSynced, onSyncError]);

  const refreshPending = useCallback(async () => {
    try {
      const ids = await listPendingOfflineEntryIds();
      setPendingEntryIds(new Set(ids));
      setPendingCount(ids.length);
      return ids;
    } catch (err) {
      console.error('Failed to list offline techlog drafts:', err);
      return [];
    }
  }, []);

  const syncOne = useCallback(async (entryId) => {
    const draft = await getOfflineDraft(entryId);
    if (!draft?.payload) {
      await removeOfflineDraft(entryId);
      return { ok: true, skipped: true };
    }

    await updateOfflineDraftStatus(entryId, 'syncing');
    try {
      const response = await axios.put(`/api/technical-log-book/${entryId}`, draft.payload);
      await removeOfflineDraft(entryId);
      if (typeof onSyncedRef.current === 'function') {
        onSyncedRef.current(entryId, response.data);
      }
      return { ok: true, data: response.data };
    } catch (error) {
      const status = isNetworkError(error) ? 'pending' : 'failed';
      const message =
        error?.response?.data?.message || error?.message || 'Sync failed';
      await updateOfflineDraftStatus(entryId, status, message);
      if (typeof onSyncErrorRef.current === 'function') {
        onSyncErrorRef.current(entryId, error);
      }
      return { ok: false, error };
    }
  }, []);

  const flushPending = useCallback(async () => {
    if (!enabled || syncingRef.current) return { synced: 0, failed: 0 };
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      return { synced: 0, failed: 0, offline: true };
    }

    syncingRef.current = true;
    setSyncing(true);
    let synced = 0;
    let failed = 0;

    try {
      const drafts = await listOfflineDrafts();
      const pending = drafts.filter(
        (d) => d && (d.syncStatus === 'pending' || d.syncStatus === 'failed')
      );
      // Oldest first
      pending.sort((a, b) => (a.updatedAt || 0) - (b.updatedAt || 0));

      for (const draft of pending) {
        const result = await syncOne(draft.entryId);
        if (result.ok) synced += 1;
        else {
          failed += 1;
          if (isNetworkError(result.error)) break;
        }
      }
    } finally {
      syncingRef.current = false;
      setSyncing(false);
      await refreshPending();
    }

    return { synced, failed };
  }, [enabled, refreshPending, syncOne]);

  useEffect(() => {
    if (!enabled) return undefined;

    const handleOnline = () => {
      setIsOnline(true);
      flushPending();
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    refreshPending();
    if (navigator.onLine) {
      flushPending();
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [enabled, flushPending, refreshPending]);

  const hasPendingFor = useCallback(
    (entryId) => {
      if (!entryId) return false;
      return pendingEntryIds.has(String(entryId));
    },
    [pendingEntryIds]
  );

  return {
    isOnline,
    pendingCount,
    pendingEntryIds,
    syncing,
    refreshPending,
    flushPending,
    syncOne,
    hasPendingFor
  };
}

export default useOfflineTechlogSync;
